import { z } from "zod";
import {
  fieldSchema,
  projectSchema,
  matchOperatorSchema,
  safePattern,
  formProfileSourceSchema,
  newField,
  parseProject,
  uid,
  type Project,
  type Field,
} from "./schema";

// Portable forms contain independent inputs. Cross-document actions bind by variable key.
export const reusableFormSchema = z
  .object({
    extra_json_data: z
      .object({
        version: z.literal(1),
        builder: projectSchema,
        linkedProjectIds: z
          .array(z.string().min(1).max(200))
          .max(100)
          .optional(),
      })
      .passthrough()
      .optional(),
    purpose: z.literal("profile-collection").optional(),
    application: z.enum(["mapped", "copy"]).optional(),
    profileSource: formProfileSourceSchema.optional(),
    fields: z
      .array(
        fieldSchema.refine(
          (f) =>
            f.source.kind === "literal" &&
            !f.calculation &&
            !f.groupId &&
            !f.visibleWhen &&
            !f.presets.length &&
            !Object.keys(f.qr?.mapping || {}).length &&
            !Object.keys(f.qr?.defaults || {}).length &&
            !Object.keys(f.map?.mapping || {}).length &&
            !Object.keys(f.map?.defaults || {}).length &&
            !Object.keys(f.map?.linked || {}).length,
          "Reusable form inputs must be independent; configure document dependencies after applying",
        ),
      )
      .min(1)
      .max(100),
  })
  .strict()
  .refine(
    (v) =>
      new Set(v.fields.map((f) => f.key)).size === v.fields.length &&
      v.fields.every((f) => f.key.trim()) &&
      new Set(v.fields.map((f) => f.id)).size === v.fields.length &&
      Object.keys(v.profileSource?.mapping || {}).every((id) =>
        v.fields.some((f) => f.id === id),
      ) &&
      (!v.purpose || (!v.profileSource && v.application !== "copy")),
    "Form variable keys must be nonempty and unique",
  );

/** Conservative, deterministic suggestions. Ambiguous candidates stay unmapped. */
export function suggestFormMapping(inputs: Field[], targets: Field[]) {
  const normalize = (s: string) =>
    s
      .normalize("NFKC")
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]/gu, "");
  const mapping: Record<string, string> = {};
  const suggestions = inputs.map((input) => {
    const compatible = targets.filter((target) =>
      formFieldCompatible(input, target),
    );
    let matches = compatible.filter((target) => target.key === input.key);
    let reason = "Exact variable key";
    if (!matches.length) {
      matches = compatible.filter(
        (target) =>
          normalize(input.key) &&
          normalize(target.key) === normalize(input.key),
      );
      reason = "Normalized variable key";
    }
    if (!matches.length) {
      matches = compatible.filter(
        (target) =>
          normalize(input.label) &&
          normalize(target.label) === normalize(input.label),
      );
      reason = "Matching label";
    }
    return {
      inputId: input.id,
      targetId: matches.length === 1 ? matches[0].id : undefined,
      reason:
        matches.length > 1
          ? "Ambiguous matches; choose a variable"
          : matches.length
            ? reason
            : "No compatible match",
    };
  });
  const counts = new Map<string, number>();
  for (const suggestion of suggestions)
    if (suggestion.targetId)
      counts.set(
        suggestion.targetId,
        (counts.get(suggestion.targetId) || 0) + 1,
      );
  for (const suggestion of suggestions) {
    if (suggestion.targetId && counts.get(suggestion.targetId) === 1)
      mapping[suggestion.inputId] = suggestion.targetId;
    else if (suggestion.targetId) {
      suggestion.targetId = undefined;
      suggestion.reason =
        "Multiple inputs match this variable; choose a variable";
    }
  }
  return { mapping, suggestions };
}
export function formFieldCompatible(input: Field, target: Field) {
  const text = ["text", "textarea", "email", "phone", "richtext"];
  return (
    input.type === target.type ||
    (text.includes(input.type) && text.includes(target.type))
  );
}

/** Capture definitions, never resolved user/order/profile data or calculated values. */
export function suggestFormFromDocument(project: Project) {
  const skipped: string[] = [];
  const fields = [...project.fields]
    .sort((a, b) => a.order - b.order)
    .filter((field) => {
      const include =
        field.editable &&
        !field.system &&
        !field.autoGenerated &&
        !field.calculation &&
        ![
          "user",
          "order",
          "form",
          "lookup",
          "dam",
          "dynamic",
          "statement",
        ].includes(field.source.kind) &&
        field.type !== "preset";
      if (!include) skipped.push(field.label);
      return include;
    })
    .slice(0, 100)
    .map((field) =>
      fieldSchema.parse({
        ...field,
        id: uid("input"),
        source: { kind: "literal" },
        groupId: undefined,
        calculation: undefined,
        visibleWhen: undefined,
        presets: [],
        qr: field.qr ? { ...field.qr, mapping: {} } : undefined,
        default: field.type === "checkbox" ? false : "",
        hidden: false,
        formDisplay: "show",
        proofName: false,
        legacy: undefined,
      }),
    );
  if (!fields.length)
    throw new Error(
      "This document has no independent editable variables to suggest. Create a form and add inputs instead.",
    );
  return {
    value: reusableFormSchema.parse({ application: "mapped", fields }),
    skipped,
    omitted: Math.max(
      0,
      project.fields.length - skipped.length - fields.length,
    ),
  };
}

/** Explicitly expose an otherwise unmapped input as an editable, artwork-independent variable. */
export function exposeFormInput(
  project: Project,
  instanceId: string,
  inputId: string,
): Project {
  const p = parseProject(project),
    instance = p.formInstances.find((i) => i.id === instanceId);
  const form = p.forms.find((f) => f.id === instance?.formId),
    input = form?.fields.find((f) => f.id === inputId);
  if (!instance || !input) throw new Error("Form input not found");
  if (instance.mapping[inputId]) return p;
  let key = input.key,
    suffix = 2;
  while (p.fields.some((f) => f.key === key)) key = `${input.key}_${suffix++}`;
  let group = p.groups.find((g) => g.id === `form_${instance.id}`);
  if (!group) {
    group = {
      id: `form_${instance.id}`,
      name: instance.label,
      column: 1,
      order: p.groups.length,
      visible: true,
      note: "",
    };
    p.groups.push(group);
    if (p.tabs.length) p.tabs[0].groupIds.push(group.id);
  }
  const field = {
    ...input,
    id: uid("field"),
    key,
    groupId: group.id,
    order: p.fields.length,
    hidden: false,
    formDisplay: "show" as const,
  };
  p.fields.push(field);
  instance.mapping[inputId] = field.id;
  instance.inputOnly = [...(instance.inputOnly || []), inputId];
  return parseProject(p);
}

export function formVisibleFieldIds(project: Project): Set<string> | undefined {
  const instances = project.formInstances.filter((i) => !i.profileOnly);
  const managed = instances.filter((i) => i.mappedOnly);
  if (instances.some((i) => i.mappedOnly === false)) return undefined;
  return managed.length
    ? new Set(instances.flatMap((i) => Object.values(i.mapping)))
    : undefined;
}
const key = z.string().min(1).max(200);
export const reusableRuleSchema = z
  .object({
    sourceKey: key,
    op: matchOperatorSchema.optional(),
    value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    show: z.array(key).max(100).default([]),
    hide: z.array(key).max(100).default([]),
    clear: z.array(key).max(100).default([]),
  })
  .strict()
  .superRefine((rule, ctx) => {
    if (rule.op === "matches")
      try {
        safePattern(String(rule.value));
      } catch {
        ctx.addIssue({
          code: "custom",
          message: "Invalid or unsafe regex",
          path: ["value"],
        });
      }
  });
export const reusablePresetSchema = z
  .object({
    options: z
      .array(
        z
          .object({
            id: key,
            label: z.string().min(1).max(200),
            match: z
              .object({
                field: key,
                op: matchOperatorSchema,
                value: z.string(),
              })
              .strict()
              .optional(),
            values: z.record(
              key,
              z.union([z.string(), z.number(), z.boolean(), z.null()]),
            ),
          })
          .strict(),
      )
      .min(1)
      .max(100),
  })
  .strict()
  .refine(
    (v) => new Set(v.options.map((o) => o.id)).size === v.options.length,
    "Preset option IDs must be unique",
  )
  .superRefine((preset, ctx) => {
    preset.options.forEach((option, i) => {
      if (option.match?.op === "matches")
        try {
          safePattern(option.match.value);
        } catch {
          ctx.addIssue({
            code: "custom",
            message: "Invalid or unsafe regex",
            path: ["options", i, "match", "value"],
          });
        }
    });
  });

export function applyReusableResource(
  project: Project,
  kind: "form" | "rule" | "preset",
  value: unknown,
  name: string,
): Project {
  const p = parseProject(project);
  const resolve = (key: string) => {
    const matches = p.fields.filter((f) => f.key === key);
    if (matches.length !== 1)
      throw new Error(
        `Variable key '${key}' must match exactly one document variable`,
      );
    return matches[0].id;
  };
  if (kind === "form") {
    const form = reusableFormSchema.parse(value),
      formId = uid("form"),
      groupId = uid("group");
    if (form.application === "mapped") {
      const { mapping } = suggestFormMapping(form.fields, p.fields);
      p.forms.push({
        id: formId,
        name,
        fields: form.fields,
        allowCreate: !form.profileSource,
        ...(form.profileSource ? { profileSource: form.profileSource } : {}),
      });
      p.formInstances.push({
        id: uid("instance"),
        formId,
        label: name,
        mapping,
        mappedOnly: true,
      });
      return parseProject(p);
    }
    const mapping: Record<string, string> = {};
    const fields = form.fields.map((f, i) => {
      let key = f.key,
        suffix = 2;
      while (p.fields.some((x) => x.key === key)) key = `${f.key}_${suffix++}`;
      const field = {
        ...f,
        id: uid("field"),
        key,
        groupId,
        order: p.fields.length + i,
      };
      mapping[f.id] = field.id;
      p.fields.push(field);
      return f;
    });
    p.groups.push({
      id: groupId,
      name,
      column: 1,
      order: p.groups.length,
      visible: true,
      note: "",
    });
    if (p.tabs.length) p.tabs[0].groupIds.push(groupId);
    p.forms.push({ id: formId, name, fields, allowCreate: true });
    p.formInstances.push({ id: uid("instance"), formId, label: name, mapping });
  } else if (kind === "rule") {
    const rule = reusableRuleSchema.parse(value);
    if (rule.op === "matches") safePattern(String(rule.value));
    p.triggers.push({
      id: uid("trigger"),
      sourceId: resolve(rule.sourceKey),
      op: rule.op,
      value: rule.value,
      show: rule.show.map(resolve),
      hide: rule.hide.map(resolve),
      clear: rule.clear.map(resolve),
      exclude: {},
    });
  } else {
    const preset = reusablePresetSchema.parse(value),
      field = newField("preset");
    field.label = name;
    while (p.fields.some((f) => f.key === field.key)) field.key += "_copy";
    field.order = p.fields.length;
    field.presets = preset.options.map((o) => ({
      ...o,
      match: o.match
        ? { ...o.match, field: resolve(o.match.field) }
        : undefined,
      values: Object.fromEntries(
        Object.entries(o.values).map(([k, v]) => [resolve(k), v]),
      ),
    }));
    p.fields.push(field);
  }
  return parseProject(p);
}
